import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { FaPlus, FaEdit, FaTrash, FaCopy } from 'react-icons/fa';
import { apiFetch } from '../api';
import { useAuth } from '../auth/AuthContext';
import { canManageContracts, canManageProducts } from '../auth/roles';
import ContractFiles from './ContractFiles';
import ContractCreateModal from './ContractCreateModal';
import ContractEditModal from './ContractEditModal';
import ProductEditModal from './ProductEditModal';
import ConfirmDeleteModal from './ConfirmDeleteModal';
import { PageHeader, QuantityProgress, DeadlineBadge, StageBadge } from './ui';
import { formatDate } from '../utils/date';
import { Modal } from 'react-bootstrap';
import ProductFiles from './ProductFiles';

function ProductContracts() {
  const { productId } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showContractModal, setShowContractModal] = useState(false);
  const [showEditProductModal, setShowEditProductModal] = useState(false);
  const [editingContract, setEditingContract] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [refreshKey, setRefreshKey] = useState(Date.now());
  const [showProductFilesModal, setShowProductFilesModal] = useState(false);
  const [filesCount, setFilesCount] = useState(0);
  const [copyContractData, setCopyContractData] = useState(null); // NEW
  const navigate = useNavigate();
  const { user } = useAuth();
  const canAddContract = canManageContracts(user?.role);
  const canEditProduct = canManageProducts(user?.role);
  const canEditContract = canManageContracts(user?.role);

  const loadProduct = useCallback((silent = false) => {
    if (!silent) { setLoading(true); setError(null); }
    return apiFetch(`/api/products/${productId}/`)
      .then((data) => {
        setProduct(data);
        setRefreshKey(Date.now());
        setLoading(false);
      })
      .catch((err) => {
        console.error(err);
        setError('Не удалось загрузить изделие');
        setLoading(false);
      });
  }, [productId]);

  const fetchFilesCount = useCallback(() => {
    apiFetch(`/api/product-files/?product=${productId}`)
      .then((data) => {
        const count = data.results ? data.results.length : data.length;
        setFilesCount(count);
      })
      .catch((err) => {
        console.error('Ошибка загрузки количества файлов:', err);
      });
  }, [productId]);

  useEffect(() => {
    loadProduct();
  }, [loadProduct]);

  useEffect(() => {
    if (product) {
      fetchFilesCount();
    }
  }, [product, fetchFilesCount]);

  const handleContractCreated = (contract) => {
    loadProduct(true);
    setShowContractModal(false);
    setCopyContractData(null);
  };

  const handleDelete = async () => {
    if (deleteTarget.type === 'product') {
      await apiFetch(`/api/products/${deleteTarget.id}/`, { method: 'DELETE' });
      navigate('/');
    } else {
      await apiFetch(`/api/contracts/${deleteTarget.id}/`, { method: 'DELETE' });
      setEditingContract(null);
      await loadProduct(true);
    }
  };

  // Обработчик копирования договора
  const handleCopyContract = (contract) => {
    setCopyContractData(contract);
    setShowContractModal(true);
  };

  // Обработчик открытия модалки для нового договора – сбрасываем копируемые данные
  const handleNewContract = () => {
    setCopyContractData(null);
    setShowContractModal(true);
  };

  if (loading) return <div className="empty-state">Загрузка...</div>;
  if (error) return <div className="empty-state text-danger">{error}</div>;
  if (!product) return <div className="empty-state">Изделие не найдено</div>;

  return (
    <>
      <PageHeader
        title={product.name}
        subtitle={`Код: ${product.code} · Договоры по изделию`}
        backLink={<Link to="/" className="app-btn mb-5 d-inline-flex">Назад к изделиям</Link>}
        actions={
          <>
            {canEditProduct && (
              <>
                <button type="button" className="app-btn" onClick={() => setShowEditProductModal(true)}>
                  <FaEdit />
                </button>
                <button
                  type="button"
                  className="app-btn app-btn-danger-outline"
                  onClick={() => setDeleteTarget({
                    type: 'product',
                    id: product.id,
                    name: `${product.name} (${product.code})`,
                  })}
                >
                  <FaTrash />
                </button>
              </>
            )}
            {canAddContract && (
              <button type="button" className="app-btn app-btn-primary" onClick={handleNewContract}>
                <FaPlus />
              </button>
            )}
          </>
        }
      />
      
      <div className="stats-row mb-4">
        <div className="stat-card">
          <div className="stat-card-label">Кол-во (собрано / всего)</div>
          <QuantityProgress done={product.done || 0} total={product.total || 0} />
        </div>
        <div className="stat-card">
          <div className="stat-card-label">Договоров</div>
          <div className="stat-card-value">{product.contracts?.length || 0}</div>
        </div>
        <div
          className="stat-card"
          onClick={() => setShowProductFilesModal(true)}
          style={{ cursor: 'pointer' }}
        >
          <div className="stat-card-label">Файлы изделия</div>
          <div className="stat-card-value">{filesCount}</div>
          <div className="small text-muted">📎 Нажмите для управления</div>
        </div>
      </div>

      <h2 className="mt-2 mb-4" style={{ fontSize: '1.5rem', fontWeight: 600, color: 'var(--text-primary)' }}>Договоры</h2>
      <div className="contracts-list">
        {product.contracts?.length > 0 ? (
          product.contracts.map((contract) => (
            <div key={contract.id} className="contract-card-full" onClick={() => navigate(`/contract/${contract.id}/stages`)} style={{ cursor: 'pointer' }}>
              <div className="contract-card-body">
                <div className="contract-card-header">
                  <span className="contract-card-number">{contract.number}</span>
                  <StageBadge stage={contract.current_stage} />
                </div>

                <div className="contract-card-details-grid">
                  <div className="contract-card-field">
                    <span className="card-label">Кол-во</span>
                    <QuantityProgress done={contract.done_quantity || 0} total={contract.quantity || 0} />
                  </div>
                  <div className="contract-card-field">
                    <span className="card-label">Период</span>
                    <span className="card-value">{formatDate(contract.start_date)} — {formatDate(contract.end_date)}</span>
                  </div>
                  <div className="contract-card-field">
                    <span className="card-label">Сумма</span>
                    <span className="card-value">{Number(contract.amount).toLocaleString('ru-RU')} ₽</span>
                  </div>
                  {contract.nearest_deadline && (
                    <div className="contract-card-field">
                      <DeadlineBadge deadline={formatDate(contract.nearest_deadline)} isOverdue={contract.is_overdue} />
                    </div>
                  )}
                </div>

                <div className="contract-card-files">
                  <ContractFiles
                    key={`${contract.id}-${refreshKey}`}
                    contractId={contract.id}
                    compact
                    showUpload={false}
                  />
                </div>

                {canEditContract && (
                  <div className="contract-card-actions" onClick={(e) => e.stopPropagation()}>
                    <button
                      type="button"
                      className="app-btn app-btn-sm btn-icon-only"
                      onClick={() => handleCopyContract(contract)}
                      title="Копировать договор"
                    >
                      <FaCopy />
                    </button>
                    <button
                      type="button"
                      className="app-btn app-btn-sm btn-icon-only"
                      onClick={() => setEditingContract(contract)}
                      title="Редактировать"
                    >
                      <FaEdit />
                    </button>
                    <button
                      type="button"
                      className="app-btn app-btn-sm btn-icon-only app-btn-danger-outline"
                      onClick={() => setDeleteTarget({ type: 'contract', id: contract.id, name: contract.number })}
                      title="Удалить"
                    >
                      <FaTrash />
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))
        ) : (
          <div className="empty-state">Нет договоров. {canAddContract && 'Нажмите «Новый договор», чтобы добавить.'}</div>
        )}
      </div>

      <ContractCreateModal
        show={showContractModal}
        onHide={() => {
          setShowContractModal(false);
          setCopyContractData(null);
        }}
        productId={productId}
        onCreated={handleContractCreated}
        initialData={copyContractData}
      />

      <ProductEditModal
        show={showEditProductModal}
        onHide={() => setShowEditProductModal(false)}
        product={product}
        onSaved={() => loadProduct(true)}
        onDelete={canEditProduct ? () => {
          setShowEditProductModal(false);
          setDeleteTarget({ type: 'product', id: product.id, name: `${product.name} (${product.code})` });
        } : undefined}
      />

      <ContractEditModal
        show={!!editingContract}
        onHide={() => setEditingContract(null)}
        contract={editingContract}
        onSaved={() => {
          setEditingContract(null);
          loadProduct(true);
        }}
        onDelete={canEditContract && editingContract ? () => {
          setDeleteTarget({ type: 'contract', id: editingContract.id, name: editingContract.number });
        } : undefined}
      />

      <ConfirmDeleteModal
        show={!!deleteTarget}
        onHide={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title={deleteTarget?.type === 'product' ? 'Удалить изделие?' : 'Удалить договор?'}
        message={
          deleteTarget?.type === 'product'
            ? 'Будут удалены все договоры, стадии, файлы и остатки, связанные с этим изделием.'
            : 'Будут удалены все стадии, остатки и история этого договора.'
        }
        itemName={deleteTarget?.name}
      />

      <Modal show={showProductFilesModal} onHide={() => setShowProductFilesModal(false)} centered size="lg">
        <Modal.Header closeButton className="app-surface">
          <Modal.Title>Файлы изделия</Modal.Title>
        </Modal.Header>
        <Modal.Body className="app-surface">
          <ProductFiles productId={productId} />
        </Modal.Body>
      </Modal>
    </>
  );
}

export default ProductContracts;